// Contrat central de Veillée. Tous les modules (moteur, Conteur IA, serveur, client)
// partagent ces types. NE PAS modifier sans accord de l'intégrateur.

export type Id = string;
/** Horodatage en millisecondes (temps réel = temps du jeu, fuseau Europe/Paris). */
export type Millis = number;

export const HOUR: Millis = 3_600_000;
export const MINUTE: Millis = 60_000;

// ───────────────────────── Caractéristiques & compétences ─────────────────────────

export type AttributeId = "FOR" | "DEX" | "CON" | "INT" | "SAG" | "CHA";
export const ATTRIBUTES: AttributeId[] = ["FOR", "DEX", "CON", "INT", "SAG", "CHA"];
export type Attributes = Record<AttributeId, number>;

export type SkillId =
  | "athletisme" | "acrobaties" | "discretion" | "escamotage"
  | "arcanes" | "histoire" | "investigation"
  | "nature" | "perception" | "survie" | "medecine" | "intuition"
  | "persuasion" | "tromperie" | "intimidation" | "representation";

export type ClassId = "guerrier" | "mage" | "rodeur" | "clerc" | "voleur" | "barde";
export const CLASS_IDS: ClassId[] = ["guerrier", "mage", "rodeur", "clerc", "voleur", "barde"];

export type ConditionId =
  | "empoisonne" | "saignement" | "etourdi" | "beni" | "affame" | "famine" | "epuise"
  | "invisible" | "protege" | "enflamme" | "gele" | "maudit" | "inspire" | "cicatrice_oubli" | "repose";

export interface ActiveCondition {
  id: ConditionId;
  /** null = jusqu'à retrait explicite */
  until: Millis | null;
  source?: string;
}

/** Comportement du personnage quand le joueur est absent (mode veille / idle). */
export type Posture = "prudent" | "travailleur" | "explorateur";

export type LifeState = "actif" | "endormi" | "en_route" | "agonie" | "mort" | "egare";

// ───────────────────────── Dés ─────────────────────────

export interface Rng {
  /** flottant dans [0, 1) */
  next(): number;
  /** entier dans [min, max] inclus */
  int(min: number, max: number): number;
  pick<T>(items: readonly T[]): T;
  chance(p: number): boolean;
  /** état interne sérialisable (à remettre dans GameState.rngState) */
  state(): number;
}

export type RollMode = "normal" | "avantage" | "desavantage";

export interface DiceRollRecord {
  label: string;            // ex. "Perception", "Attaque — Épée longue", "Dégâts"
  notation: string;         // ex. "1d20+5", "2d6+3"
  rolls: number[];          // tous les dés lancés (pour l'animation)
  kept: number;             // résultat retenu du d20 (ou somme des dés hors d20)
  modifier: number;
  total: number;
  dc?: number;
  success?: boolean;
  crit?: "succes" | "echec" | null;
  mode: RollMode;
}

// ───────────────────────── Objets ─────────────────────────

export type Rarity = "commun" | "peu_commun" | "rare" | "epique" | "legendaire";
export type ItemKind = "arme" | "armure" | "bouclier" | "consommable" | "nourriture" | "materiau" | "quete" | "tresor" | "outil";
export type EquipSlot = "main" | "secondaire" | "armure" | "accessoire";
export type BonusKey = AttributeId | "attaque" | "degats" | "ca" | "pvmax" | "manamax";

export interface ItemUse {
  heal?: string;            // notation de dés, ex. "2d4+2"
  mana?: number;
  satiety?: number;
  energy?: number;
  cure?: ConditionId[];
  addCondition?: { id: ConditionId; hours: number };
}

export interface ItemDef {
  key: string;              // identifiant de catalogue (kebab-case) ou "unique-<id>" pour un objet unique
  name: string;
  icon: string;             // un emoji
  kind: ItemKind;
  rarity: Rarity;
  description: string;
  weight: number;           // en kg
  value: number;            // prix de base en pièces d'or
  stackable: boolean;
  slot?: EquipSlot;
  damage?: string;          // armes : notation de dés, ex. "1d8"
  damageAttr?: AttributeId; // FOR ou DEX
  twoHanded?: boolean;
  armor?: number;           // bonus de CA (armure/bouclier)
  bonuses?: Partial<Record<BonusKey, number>>;
  use?: ItemUse;            // consommables & nourriture
}

export interface ItemInstance {
  iid: Id;
  key: string;
  qty: number;
  equipped?: EquipSlot | null;
  /** objet unique créé par le Conteur (validé par le moteur) */
  custom?: ItemDef | null;
}

// ───────────────────────── Sorts ─────────────────────────

export type SpellKind = "degats" | "soin" | "buff" | "debuff" | "utilitaire";
export type SpellTarget = "soi" | "allie" | "ennemi" | "zone";

export interface SpellDef {
  key: string;
  name: string;
  icon: string;
  classes: ClassId[];
  level: number;            // niveau de personnage requis
  mana: number;
  kind: SpellKind;
  dice?: string;            // dégâts ou soins, ex. "3d6"
  attr: AttributeId;        // caractéristique d'incantation (modificateur ajouté aux dés)
  condition?: { id: ConditionId; hours: number };
  target: SpellTarget;
  description: string;
  combatOnly?: boolean;
}

// ───────────────────────── Personnages & joueurs ─────────────────────────

export interface PlayStyleStats {
  combat: number; social: number; exploration: number; magie: number; ruse: number; actions: number;
}

export interface TravelState { from: Id; to: Id; departAt: Millis; arriveAt: Millis }

export interface Character {
  id: Id;
  playerId: Id;
  name: string;
  classId: ClassId;
  level: number;
  xp: number;                // XP cumulée
  attributes: Attributes;
  hp: number; hpMax: number;
  mana: number; manaMax: number;
  energy: number; energyMax: number;
  satiety: number;           // 0..100
  flame: number;             // 0..3 — « Flamme » : s'éteint quand le joueur ne joue pas
  gold: number;
  inventory: ItemInstance[];
  spells: string[];          // clés de sorts connus
  conditions: ActiveCondition[];
  locationId: Id;
  travel: TravelState | null;
  lifeState: LifeState;
  agonieUntil: Millis | null;
  sleepUntil: Millis | null;
  posture: Posture;
  concept: string;           // ex. « forgeronne bourrue qui cherche son frère »
  backstory: string;
  appearance: { color: string; sigil: string }; // couleur hex + emoji/sigle
  createdAt: Millis;
  lastActionAt: Millis;      // dernière action volontaire du joueur
  lastIdleAt: Millis;        // dernière résolution de routine d'absence
  lastTickAt: Millis;        // dernier passage de survie
  partyId: Id | null;
  metCharacterIds: Id[];
  knownLocationIds: Id[];    // brouillard de guerre
  combatId: Id | null;
  dungeonRunId: Id | null;
  heirOf?: Id | null;        // héritier d'un personnage mort
  deathAt?: Millis | null;
  stats: PlayStyleStats;     // profil de jeu (adaptation du Conteur)
  suggestions: string[];     // dernières actions suggérées par le Conteur
  busy: boolean;             // une action est en cours de résolution par le Conteur
}

export interface PushSubscriptionData { endpoint: string; keys: { p256dh: string; auth: string } }

export interface Player {
  id: Id;
  name: string;
  token: string;             // secret d'authentification (jamais envoyé aux autres joueurs)
  joinedAt: Millis;
  characterId: Id | null;
  isHost: boolean;
  lastSeenAt: Millis;
  push?: PushSubscriptionData | null;
  /** jeton APNs de son iPhone, si l'appli a reçu l'autorisation de notifier */
  apns?: string | null;
}

/** Ce que le joueur envoie pour créer son personnage. */
export interface CharacterDraft {
  name: string;
  classId: ClassId;
  concept: string;
  backstory: string;
  color: string;
  sigil: string;
  posture: Posture;
}

// ───────────────────────── Monde ─────────────────────────

export type Biome = "plaine" | "foret" | "montagne" | "marais" | "desert" | "cote" | "toundra" | "souterrain" | "volcan" | "jungle";
export type LocationKind =
  | "village" | "ville" | "hameau" | "foret" | "ruines" | "donjon" | "grotte" | "lac"
  | "col" | "sanctuaire" | "tour" | "camp" | "port" | "tombeau" | "chateau" | "clairiere";

export interface Region { id: Id; name: string; biome: Biome; description: string }

export interface Location {
  id: Id;
  name: string;
  kind: LocationKind;
  biome: Biome;
  regionId: Id;
  x: number; y: number;      // 0..1000 sur la carte
  description: string;
  danger: number;            // 0 (sûr) .. 5 (mortel)
  safe: boolean;             // repos sûr possible
  hasMerchant: boolean;
  tags: string[];
  dungeonId: Id | null;
  isStartingVillage: boolean;
}

export interface Edge { a: Id; b: Id; hours: number; kind: "route" | "sentier" | "riviere" | "passage" }

export interface Npc {
  id: Id;
  name: string;
  role: string;              // ex. « aubergiste », « prêtresse de la Lune »
  description: string;
  locationId: Id | null;
  attitude: number;          // -3 (hostile) .. +3 (dévoué), par défaut 0
  status: "vivant" | "mort" | "disparu";
  traits: string[];
  secret?: string | null;
  knownBy: Id[];             // characterIds qui l'ont rencontré
  notes: string[];           // mémoire du PNJ (faits marquants)
}

export interface Faction { id: Id; name: string; goal: string; description: string }

/** Horloge de progression (menaces, intrigues) — façon Blades in the Dark. */
export interface Clock {
  id: Id; name: string; description: string;
  segments: number; filled: number; visible: boolean; onComplete: string;
}

export interface Quest {
  id: Id;
  title: string;
  description: string;
  giverNpcId: Id | null;
  characterIds: Id[];
  status: "active" | "terminee" | "echouee";
  objectives: { text: string; done: boolean }[];
  reward: { xp: number; gold: number; itemKeys: string[] };
  createdAt: Millis;
}

export interface CanonFact { id: Id; text: string; tags: string[]; at: Millis }

export interface WorldBible {
  title: string;
  tagline: string;
  tone: string;
  premise: string;           // situation de départ
  cosmology: string;         // magie, dieux, histoire
  threat: { name: string; description: string; clockId: Id };
  secrets: string[];         // vérités cachées, révélées petit à petit
  acts: { title: string; summary: string }[]; // arc principal en 3 à 5 actes
  currentAct: number;        // index dans acts
  convergence: string;       // ce qui va réunir les héros dispersés
  styleGuide: string;        // consignes de ton et de style du récit
}

export type TonePreset = "heroique" | "sombre" | "feerique" | "humoristique" | "steampunk" | "pirates" | "post_apo" | "libre";

// ───────────────────────── Combats & donjons ─────────────────────────

export interface MonsterDef {
  key: string;
  name: string;
  icon: string;
  tier: number;              // 1..10, ~ niveau de héros conseillé
  biomes: Biome[];
  hp: number;
  ca: number;
  attack: number;            // bonus au jet d'attaque
  damage: string;            // notation, ex. "1d6+2"
  xp: number;
  gold: string;              // notation, ex. "2d6"
  loot: { key: string; chance: number }[];
  traits: string[];
  behavior: "agressif" | "lache" | "tactique" | "bestial";
  description: string;
  boss?: boolean;
}

export interface Combatant {
  cid: Id;
  side: "heros" | "ennemis";
  characterId?: Id | null;
  monsterKey?: string | null;
  name: string;
  icon: string;
  hp: number; hpMax: number;
  ca: number;
  initiative: number;
  conditions: ActiveCondition[];
  fled: boolean;
  defending: boolean;
}

export type CombatChoice =
  | { kind: "attaque"; targetCid: Id }
  | { kind: "sort"; spellKey: string; targetCid?: Id | null }
  | { kind: "objet"; iid: Id; targetCid?: Id | null }
  | { kind: "defense" }
  | { kind: "fuite" };

export interface Combat {
  id: Id;
  locationId: Id;
  startedAt: Millis;
  round: number;
  combatants: Combatant[];
  pendingChoices: Record<Id, CombatChoice>; // clé = characterId
  roundDeadline: Millis;
  status: "en_cours" | "victoire" | "defaite" | "fuite";
  log: string[];
  dungeonRunId: Id | null;
  xpPool: number;
}

export type RoomKind = "entree" | "couloir" | "piege" | "enigme" | "combat" | "tresor" | "repos" | "boss";

export interface DungeonRoom {
  id: Id;
  kind: RoomKind;
  name: string;
  description: string;
  monsters: { key: string; count: number }[];
  trap: { skill: SkillId; dc: number; damage: string } | null;
  puzzle: { text: string; skill: SkillId; dc: number } | null;
  loot: string[];            // clés d'objets
  gold: number;
  cleared: boolean;
  next: Id[];                // salles suivantes
}

export interface Dungeon {
  id: Id;
  locationId: Id;
  name: string;
  theme: string;
  level: number;
  rooms: DungeonRoom[];
  bossName: string;
  lore: string;
}

export interface DungeonRun {
  id: Id;
  dungeonId: Id;
  characterIds: Id[];
  roomId: Id;
  startedAt: Millis;
  status: "en_cours" | "termine" | "abandonne";
  visited: Id[];
}

// ───────────────────────── Sollicitations (questions avec échéance) ─────────────────────────

export type Urgency = "immediat" | "pressant" | "tranquille";
export type SollicitationKind = "combat" | "evenement" | "dialogue" | "rencontre" | "quete" | "survie" | "donjon";

export interface SollicitationOption { id: string; label: string; hint?: string | null }

export interface Sollicitation {
  id: Id;
  characterId: Id;
  createdAt: Millis;
  deadline: Millis;
  urgency: Urgency;
  kind: SollicitationKind;
  title: string;
  prompt: string;
  options: SollicitationOption[];
  allowFreeText: boolean;
  defaultOptionId: string;
  consequenceIfIgnored: string;
  context: { combatId?: Id | null; npcId?: Id | null; questId?: Id | null; dungeonRunId?: Id | null };
  status: "ouverte" | "repondue" | "expiree";
  answer: { optionId?: string | null; text?: string | null; at: Millis; auto: boolean } | null;
}

/** Ce que le Conteur propose ; le moteur le transforme en Sollicitation (id, échéance…). */
export interface SollicitationSpec {
  title: string;
  prompt: string;
  options: { label: string; hint?: string | null }[];
  urgency: Urgency;
  kind: SollicitationKind;
  defaultOptionIndex: number;
  consequenceIfIgnored: string;
}

// ───────────────────────── Événements (fil du récit) ─────────────────────────

export type Visibility =
  | { scope: "global" }
  | { scope: "location"; locationId: Id }
  | { scope: "characters"; characterIds: Id[] };

export type EventKind =
  | "narration" | "action" | "jet" | "combat" | "systeme" | "voyage" | "rencontre" | "mort"
  | "niveau" | "butin" | "chronique" | "monde" | "sollicitation" | "repos" | "donjon";

export interface GameEvent {
  id: Id;
  at: Millis;
  kind: EventKind;
  visibility: Visibility;
  actorId?: Id | null;       // characterId
  title?: string | null;
  text: string;
  dice?: DiceRollRecord[] | null;
  icon?: string | null;
}

export interface ChatMessage { id: Id; at: Millis; playerId: Id; name: string; color: string; text: string }

export interface Chapter { id: Id; title: string; summary: string; from: Millis; to: Millis }

export interface Party { id: Id; name: string; memberIds: Id[]; leaderId: Id; createdAt: Millis }

// ───────────────────────── État complet d'un monde ─────────────────────────

export interface GameState {
  version: 1;
  worldId: Id;
  code: string;              // code d'invitation (6 caractères)
  name: string;
  createdAt: Millis;
  rngState: number;
  idCounter: number;
  /** conteur absent : Claude (les mondes d'avant le mode ChatGPT) */
  setup: { pitch: string; tone: TonePreset; conteur?: import("./chatgpt").ConteurKind };
  status: "genese" | "pret" | "erreur";
  statusMessage: string | null;
  bible: WorldBible | null;
  regions: Region[];
  locations: Record<Id, Location>;
  edges: Edge[];
  npcs: Record<Id, Npc>;
  factions: Faction[];
  clocks: Record<Id, Clock>;
  quests: Record<Id, Quest>;
  canon: CanonFact[];
  players: Record<Id, Player>;
  characters: Record<Id, Character>;
  graveyard: Character[];
  parties: Record<Id, Party>;
  combats: Record<Id, Combat>;
  dungeons: Record<Id, Dungeon>;
  dungeonRuns: Record<Id, DungeonRun>;
  sollicitations: Record<Id, Sollicitation>;
  chapters: Chapter[];
  events: GameEvent[];       // fil récent (le moteur garde les 400 derniers)
  chat: ChatMessage[];       // discussion hors-jeu (les 200 derniers)
  lastDirectorAt: Millis;
  lastChronicleAt: Millis;
  lastTickAt: Millis;
}

/** Contexte passé à toutes les fonctions du moteur qui ont besoin du temps ou du hasard. */
export interface Ctx { now: Millis; rng: Rng }

// ───────────────────────── Effets proposés par le Conteur ─────────────────────────
// Le Conteur ne modifie JAMAIS l'état directement : il propose des effets, le moteur
// (src/engine/effects.ts) les valide, les borne selon les règles, puis les applique.

export type EffectTarget = "self" | Id; // "self" = le personnage qui agit, sinon characterId

export interface LocationSpec {
  name: string;
  kind: LocationKind;
  biome: Biome;
  description: string;
  danger: number;
  safe: boolean;
  hasMerchant: boolean;
  tags: string[];
  isStartingVillage: boolean;
  /** index dans la liste des régions (genèse) */
  regionIndex?: number | null;
}

export interface NpcSpec {
  name: string;
  role: string;
  description: string;
  /** nom ou id d'un lieu existant ; null = lieu de la scène */
  location?: string | null;
  traits: string[];
  secret?: string | null;
  attitude: number;
}

export interface QuestSpec {
  title: string;
  description: string;
  objectives: string[];
  rewardXp: number;
  rewardGold: number;
  rewardItemKeys: string[];
  giverNpcId?: Id | null;
}

export interface CustomItemSpec {
  name: string;
  icon: string;
  kind: ItemKind;
  rarity: Rarity;
  description: string;
  slot?: EquipSlot | null;
  damage?: string | null;
  damageAttr?: AttributeId | null;
  armor?: number | null;
  bonuses?: Partial<Record<BonusKey, number>> | null;
  use?: ItemUse | null;
  value: number;
}

export interface DungeonSpec {
  name: string;
  theme: string;
  lore: string;
  bossName: string;
  /** lieu existant (nom ou id) où se trouve l'entrée ; sinon un nouveau lieu relié à la scène */
  location?: string | null;
  rooms: number;             // 5..9
}

export type Effect =
  | { kind: "hp"; target: EffectTarget; delta: number; reason: string }
  | { kind: "mana"; target: EffectTarget; delta: number; reason: string }
  | { kind: "energy"; target: EffectTarget; delta: number; reason: string }
  | { kind: "satiety"; target: EffectTarget; delta: number; reason: string }
  | { kind: "gold"; target: EffectTarget; delta: number; reason: string }
  | { kind: "xp"; target: EffectTarget; amount: number; reason: string }
  | { kind: "item_gain"; target: EffectTarget; itemKey: string; qty: number }
  | { kind: "item_custom"; target: EffectTarget; item: CustomItemSpec }
  | { kind: "item_lose"; target: EffectTarget; itemKey: string; qty: number }
  | { kind: "condition_add"; target: EffectTarget; condition: ConditionId; hours: number }
  | { kind: "condition_remove"; target: EffectTarget; condition: ConditionId }
  | { kind: "move"; target: EffectTarget; toLocation: string }
  | { kind: "location_create"; spec: LocationSpec; travelHours: number }
  | { kind: "location_reveal"; location: string }
  | { kind: "npc_create"; spec: NpcSpec }
  | { kind: "npc_update"; npcId: Id; attitudeDelta?: number | null; status?: Npc["status"] | null; note?: string | null; toLocation?: string | null }
  | { kind: "canon_add"; text: string; tags: string[] }
  | { kind: "quest_add"; spec: QuestSpec }
  | { kind: "quest_progress"; questId: Id; objectiveIndex?: number | null; note: string; complete?: boolean | null; failed?: boolean | null }
  | { kind: "clock_tick"; clockId: Id; segments: number; reason: string }
  | { kind: "combat_start"; monsters: { key: string; count: number }[]; ambush: boolean }
  | { kind: "sollicitation"; target: EffectTarget; spec: SollicitationSpec }
  | { kind: "dungeon_create"; spec: DungeonSpec };

export interface ApplyResult {
  events: GameEvent[];
  applied: string[];                           // descriptions courtes des effets appliqués
  rejected: { effect: Effect; reason: string }[];
}

// ───────────────────────── Commandes des joueurs (WebSocket) ─────────────────────────

export type ClientMessage =
  | { t: "create_character"; draft: CharacterDraft }
  | { t: "action"; text: string }
  | { t: "answer"; sollicitationId: Id; optionId?: string | null; text?: string | null }
  | { t: "combat_choice"; choice: CombatChoice }
  | { t: "travel"; toLocationId: Id }
  | { t: "use_item"; iid: Id }
  | { t: "equip"; iid: Id }
  | { t: "unequip"; iid: Id }
  | { t: "drop"; iid: Id }
  | { t: "give"; iid: Id; toCharacterId: Id }
  | { t: "buy"; itemKey: string }
  | { t: "sell"; iid: Id }
  | { t: "cast"; spellKey: string; targetCharacterId?: Id | null }
  | { t: "sleep" }
  | { t: "wake" }
  | { t: "set_posture"; posture: Posture }
  | { t: "party_invite"; characterId: Id }
  | { t: "party_leave" }
  | { t: "dungeon_enter" }
  | { t: "dungeon_advance"; roomId?: Id | null }
  | { t: "dungeon_leave" }
  | { t: "cling_to_life" }
  | { t: "rebirth"; draft: CharacterDraft }
  | { t: "chat"; text: string }
  | { t: "push_subscribe"; subscription: PushSubscriptionData }
  | { t: "apns_register"; token: string }
  | { t: "chatgpt_connect" }
  | { t: "chatgpt_poll" }
  | { t: "chatgpt_disconnect" }
  | { t: "ping" };

export type ServerMessage =
  | { t: "view"; view: ClientView }
  | { t: "dice"; rolls: DiceRollRecord[]; actorName: string }
  | { t: "gm_busy"; busy: boolean; label: string }
  | { t: "toast"; text: string; tone: "info" | "succes" | "danger" }
  | { t: "error"; message: string }
  | { t: "pong" };

/** Résultat synchrone d'une commande moteur (avant éventuel appel au Conteur). */
export interface CommandOutcome {
  ok: boolean;
  error?: string | null;
  events: GameEvent[];
  dice: DiceRollRecord[];
  toasts: { text: string; tone: "info" | "succes" | "danger" }[];
  /** travail à confier au Conteur (le serveur l'exécute de façon asynchrone) */
  gmTasks: GmTask[];
}

export type GmTask =
  | { kind: "intro"; characterId: Id }
  | { kind: "action"; characterId: Id; text: string; sollicitationId?: Id | null; optionLabel?: string | null }
  | { kind: "combat_round"; combatId: Id; summary: string[] }
  | { kind: "meeting"; characterIds: Id[]; locationId: Id }
  | { kind: "dungeon_room"; runId: Id; roomId: Id; summary: string[] };

// ───────────────────────── Vue envoyée à un joueur ─────────────────────────

export interface ItemView extends ItemInstance { def: ItemDef }

export interface CharacterView {
  id: Id; name: string; classId: ClassId; className: string; level: number;
  xp: number; xpForNext: number; xpForCurrent: number;
  attributes: Attributes; modifiers: Attributes;
  hp: number; hpMax: number; mana: number; manaMax: number;
  energy: number; energyMax: number; satiety: number; flame: number; gold: number;
  ca: number; attackBonus: number; proficiency: number;
  skills: Record<SkillId, number>;
  inventory: ItemView[];
  spells: SpellDef[];
  conditions: ActiveCondition[];
  lifeState: LifeState; agonieUntil: Millis | null; sleepUntil: Millis | null;
  travel: (TravelState & { fromName: string; toName: string }) | null;
  posture: Posture;
  concept: string; backstory: string; appearance: { color: string; sigil: string };
  lastActionAt: Millis;
  /** échéance du « serment des 24 h » : agir avant cette date pour ne pas dériver */
  driftAt: Millis;
  partyId: Id | null;
  suggestions: string[];
  busy: boolean;
  carried: number; capacity: number;
}

export interface OtherCharacterView {
  id: Id; playerName: string; name: string; classId: ClassId; level: number;
  appearance: { color: string; sigil: string };
  met: boolean;               // déjà rencontré par mon personnage
  sameLocation: boolean;
  locationName: string | null; // connu seulement si rencontré ou même lieu
  lifeState: LifeState;
  hpRatio: number | null;      // 0..1 si rencontré
  online: boolean;
  /** groupe du héros, seulement si rencontré */
  partyId: Id | null;
}

export interface MapLocationView {
  id: Id; name: string; kind: LocationKind; biome: Biome; x: number; y: number;
  danger: number; known: boolean; visited: boolean; here: boolean;
  /** personnages rencontrés/visibles présents ici (sigles) */
  markers: { characterId: Id; sigil: string; color: string; name: string }[];
  travelHours: number | null; // depuis ma position si voisin direct
  description: string | null;
}

export interface CombatView {
  id: Id; round: number; roundDeadline: Millis; status: Combat["status"];
  combatants: Combatant[];
  myCid: Id | null;
  myChoice: CombatChoice | null;
  waitingFor: string[];       // noms des héros qui n'ont pas encore choisi
  log: string[];
}

export interface DungeonView {
  runId: Id; name: string; theme: string; level: number;
  room: DungeonRoom; visitedCount: number; totalRooms: number;
  members: string[];
}

export interface ClientView {
  now: Millis;
  world: {
    id: Id; code: string; name: string; status: GameState["status"]; statusMessage: string | null;
    title: string | null; tagline: string | null;
    tone: TonePreset;          // ton choisi à la création (couleur de la musique)
    act: { index: number; title: string; total: number } | null;
    threat: { name: string; filled: number; segments: number } | null;
    playerCount: number;
  };
  me: { playerId: Id; name: string; isHost: boolean; hasCharacter: boolean };
  character: CharacterView | null;
  location: (Location & { exits: { id: Id; name: string; hours: number; kind: Edge["kind"] }[] }) | null;
  npcsHere: { id: Id; name: string; role: string; attitude: number; description: string }[];
  othersHere: { id: Id; name: string; sigil: string; color: string }[];
  map: { locations: MapLocationView[]; edges: Edge[] };
  others: OtherCharacterView[];
  sollicitations: Sollicitation[];
  combat: CombatView | null;
  dungeon: DungeonView | null;
  merchant: { items: { key: string; name: string; icon: string; price: number; description: string }[]; sellRate: number } | null;
  quests: Quest[];
  clocks: Clock[];
  chapters: Chapter[];
  feed: GameEvent[];
  chat: ChatMessage[];
  graveyard: { name: string; className: string; level: number; deathAt: Millis | null }[];
  gmBusy: boolean;
  /** monde au ChatGPT de son créateur : l'état de la connexion (null pour un monde Claude) */
  chatgpt: import("./chatgpt").ChatGptLinkView | null;
}
